import React, { useCallback, useEffect, useState } from "react";
import {
  MONTHLY_REPORT_REVIEW_WINDOW_HOURS as REPORT_REVIEW_WINDOW_HOURS,
  type MonthlyReportId,
  type MonthlyReportRecord,
  type OperatorReportRow,
  type SiteId,
} from "@siteagain/shared-types";

import type { Locale } from "../i18n";
import { MonthlyReportView, ReportDialog } from "../monthly-report";
import type { OperatorCopy } from "./copy";
import { formatDateTime, formatMonth, operatorFetch, operatorPost, relativeTime } from "./format";

export type ReportDecision = "approve" | "suppress";

/**
 * The review queue for monthly reports.
 *
 * A draft cannot be approved from the list: the operator opens it and reads it exactly as the
 * customer will, and the decision buttons sit under the report. The auto-approval time shown
 * is the real rule (a draft nobody reviewed for 48 hours), never a made-up countdown.
 */
export function ReportsPanel({
  rows,
  loadError,
  copy,
  locale,
  now,
  scopeSiteId,
  onOpen,
}: {
  rows: OperatorReportRow[] | null;
  loadError: string;
  copy: OperatorCopy;
  locale: Locale;
  now: number;
  scopeSiteId: SiteId | null;
  onOpen(reportId: MonthlyReportId): void;
}) {
  const t = copy.reports;
  const visible = (rows ?? []).filter((row) => scopeSiteId === null || row.siteId === scopeSiteId);
  const drafts = visible.filter((row) => row.status === "draft");
  const history = visible.filter((row) => row.status !== "draft");

  return (
    <section className="mc-panel mr-panel" aria-labelledby="mr-panel-title">
      <header className="mc-panel-head">
        <div>
          <small>{t.subtitle}</small>
          <h2 id="mr-panel-title">{t.title}</h2>
        </div>
      </header>

      {loadError && <p className="mc-inline-error" role="alert">{loadError}</p>}
      {rows === null && !loadError && <div className="mc-skeleton" aria-hidden="true"><i /><i /><i /></div>}

      {rows !== null && (
        <>
          <h3 className="mr-subhead">{t.awaiting} <span className="mc-count">{drafts.length}</span></h3>
          {drafts.length === 0 ? (
            <p className="mc-empty-line">{t.noneAwaiting}</p>
          ) : (
            <ul className="mr-queue">
              {drafts.map((row) => {
                const autoAt = new Date(new Date(row.createdAt).getTime() + REPORT_REVIEW_WINDOW_HOURS * 3_600_000).toISOString();
                return (
                  <li key={row.id}>
                    <div className="mr-queue-main">
                      <strong>{row.siteName}</strong>
                      <span>{formatMonth(row.month, locale)}</span>
                      {row.quiet
                        ? <span className="mc-chip">{t.quiet}</span>
                        : <span className="mr-queue-totals">{row.totals.shipped} {t.columns.shipped.toLowerCase()} · {row.totals.rolledBack} {t.columns.rolledBack.toLowerCase()} · {row.totals.measured} {t.columns.measured.toLowerCase()}</span>}
                    </div>
                    <small>{t.autoApproval(formatDateTime(autoAt, locale))}</small>
                    <div className="mc-actions">
                      <button type="button" className="primary" onClick={() => onOpen(row.id)}>{t.review}</button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <h3 className="mr-subhead">{t.history}</h3>
          {history.length === 0 ? (
            <p className="mc-empty-line">{t.none}</p>
          ) : (
            <div className="mc-table-wrap">
              <table className="mc-table">
                <thead>
                  <tr>
                    <th>{t.columns.site}</th>
                    <th>{t.columns.month}</th>
                    <th>{t.columns.status}</th>
                    <th>{t.columns.email}</th>
                    <th>{t.columns.shipped}</th>
                    <th><span className="mc-visually-hidden">{copy.action.open}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id}>
                      <td>{row.siteName}</td>
                      <td>{formatMonth(row.month, locale)}{row.quiet && <> <span className="mc-chip">{t.quiet}</span></>}</td>
                      <td>
                        <span className={`mc-chip report-${row.status}`}>{t.status[row.status]}</span>
                        {row.reviewedAt && <small className="mr-reviewed">{row.reviewedBy ? t.reviewedBy(row.reviewedBy, relativeTime(row.reviewedAt, locale, now)) : t.autoReviewed(relativeTime(row.reviewedAt, locale, now))}</small>}
                      </td>
                      <td>
                        <span className={`mc-chip email-${row.emailStatus}`}>{t.email[row.emailStatus]}</span>
                        {row.emailStatus !== "sent" && row.emailDetail && <small className="mr-reviewed">{row.emailDetail}</small>}
                      </td>
                      <td>{row.totals.shipped}</td>
                      <td><button type="button" onClick={() => onOpen(row.id)}>{copy.action.open}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One report, as the customer reads it, with the review trail above and the decision under it.
 * Opened from the review queue, from a site's drawer, or from a fleet row.
 */
export function ReportModal({
  reportId,
  copy,
  locale,
  now,
  onClose,
  onDecided,
}: {
  reportId: MonthlyReportId;
  copy: OperatorCopy;
  locale: Locale;
  now: number;
  onClose(): void;
  onDecided(decision: ReportDecision, record: MonthlyReportRecord): void;
}) {
  const t = copy.reports;
  const [record, setRecord] = useState<MonthlyReportRecord | null>(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [confirmingSuppress, setConfirmingSuppress] = useState(false);
  const [busy, setBusy] = useState<ReportDecision | null>(null);
  const load = useCallback(async () => {
    try {
      const response = await operatorFetch<{ report: MonthlyReportRecord }>(`/operator/reports/${reportId}`);
      setRecord(response.report);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t.loadFailed);
    }
  }, [reportId, t.loadFailed]);

  useEffect(() => { setRecord(null); setNote(""); setConfirmingSuppress(false); void load(); }, [load]);
  async function decide(decision: ReportDecision) {
    setBusy(decision);
    setError("");
    try {
      const response = await operatorPost<{ report: MonthlyReportRecord }>(
        `/operator/reports/${reportId}/${decision}`,
        note.trim() ? { note: note.trim() } : {},
      );
      setRecord(response.report);
      setConfirmingSuppress(false);
      onDecided(decision, response.report);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t.loadFailed);
      // The report may have been decided elsewhere (a second operator, the automatic approval).
      void load();
    } finally {
      setBusy(null);
    }
  }

  const footer = record?.status === "draft" ? (
    <>
      <p>{t.reviewHint}</p>
      {record.quiet && <p>{t.quietNote}</p>}
      <p>{t.autoApproval(formatDateTime(new Date(new Date(record.createdAt).getTime() + REPORT_REVIEW_WINDOW_HOURS * 3_600_000).toISOString(), locale))}</p>
      <div className="mr-review-row">
        <label className="mr-note-field">
          <span>{t.note}</span>
          <textarea value={note} maxLength={500} rows={1} onChange={(event) => setNote(event.target.value)} />
        </label>
        {confirmingSuppress ? (
          <div className="mc-confirm" role="alert">
            <p>{t.suppressConfirm}</p>
            <div className="mc-actions">
              <button type="button" className="danger" disabled={busy !== null} onClick={() => void decide("suppress")}>{busy === "suppress" ? copy.action.working : t.suppress}</button>
              <button type="button" onClick={() => setConfirmingSuppress(false)}>{copy.action.cancel}</button>
            </div>
          </div>
        ) : (
          <div className="mc-actions">
            <button type="button" className="primary" disabled={busy !== null} onClick={() => void decide("approve")}>{busy === "approve" ? copy.action.working : t.approve}</button>
            <button type="button" disabled={busy !== null} onClick={() => setConfirmingSuppress(true)}>{t.suppress}</button>
          </div>
        )}
      </div>
    </>
  ) : undefined;

  return (
    <ReportDialog
      label={record ? `${t.title}: ${record.report.siteName}` : t.title}
      eyebrow={record ? `${record.report.siteName} · ${formatMonth(record.month, locale)}` : t.title}
      badges={record && (
        <>
          <span className={`mc-chip report-${record.status}`}>{t.status[record.status]}</span>
          <span className={`mc-chip email-${record.emailStatus}`}>{t.email[record.emailStatus]}</span>
          {record.quiet && <span className="mc-chip">{t.quiet}</span>}
        </>
      )}
      closeLabel={t.close}
      onClose={onClose}
      footer={footer}
    >
      {error && <p className="mc-inline-error" role="alert">{error}</p>}
      {!record && !error && <div className="mc-skeleton" aria-hidden="true"><i /><i /><i /></div>}
      {record && (
        <>
          {record.status !== "draft" && (
            <p className="mr-trail">
              {record.status === "suppressed" && record.reviewedAt
                ? t.withheldBy(record.reviewedBy ?? "—", relativeTime(record.reviewedAt, locale, now))
                : record.reviewedAt
                  ? record.reviewedBy
                    ? t.reviewedBy(record.reviewedBy, relativeTime(record.reviewedAt, locale, now))
                    : t.autoReviewed(relativeTime(record.reviewedAt, locale, now))
                  : null}
              {record.reviewNote && <> — “{record.reviewNote}”</>}
              {record.emailDetail && record.emailStatus !== "sent" && <> — {record.emailDetail}</>}
            </p>
          )}
          <MonthlyReportView report={record.report} showPrint />
        </>
      )}
    </ReportDialog>
  );
}
